import { z } from 'zod'

import { hasCode } from '@/api/errors'
import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { depthSectorsQueryOptions, marketDepthQueryOptions } from '@/features/depth/api/queries'
import { marketStateIsOpen } from '@/features/market/api/marketMappers'
import { marketStatusQueryOptions } from '@/hooks/useMarketStatus'
import type { RouterContext } from '@/routes/layouts/root'
import { pageSizeSchema, tableQuerySchema } from '@/schemas/table'

const DEFAULT_SECTOR = 'banking'

/**
 * `depthQuerySchema` (schemas/depth.ts) leaves `sector` and `symbols`
 * required — correct for the hook, which always needs a selection, but the
 * route needs a default of its own so a bare `/market/depth` still resolves.
 * Moved here from the screen file, as `stockSearchSchema` moved into
 * `StockScreen.loader.ts`, so the loader below and the route's
 * `validateSearch` read the one schema and can never disagree about what a
 * URL means.
 *
 * `symbols` is optional rather than defaulted to an empty array, because
 * absent IS the empty selection and the service reads that as "the whole
 * sector". A default would put `?symbols=%5B%5D` in the URL the first time
 * anything else navigated, and a member who cleared the picker would be
 * sharing a link that states a filter it does not have.
 */
export const depthSearchSchema = tableQuerySchema.extend({
  /** Six a page, the mock's: three rows of two books. The page has no "Rows per page" menu. */
  pageSize: pageSizeSchema.default(6),
  sector: z.string().min(1).default(DEFAULT_SECTOR),
  symbols: z.array(z.string().min(1)).optional(),
})

/**
 * The `/market/depth` loader.
 *
 * MAIN, awaited unconditionally (hover preload included, same as every other
 * converted screen's main data): market status first, and — only once that
 * says OPEN — the order books for the entry URL's query.
 *
 * Status, not the sector list, is what belongs here. The sector list cannot
 * fail THIS page — it is the filter's own option list, nothing else on
 * screen reads it — so awaiting it, the old shape, meant a sectors 5xx
 * replaced the whole page with the route error page for a failure that
 * should have left the filter merely empty. Status decides whether there is
 * a grid to draw at all, which the sector list never did: `DepthGrid` used
 * to run its own `isLoading` skeleton for a beat after the old loader
 * resolved, because `useMarketStatus` had not necessarily settled by the
 * time the screen mounted — the gap that let a closed market show ten
 * `h-[360px]` skeletons before collapsing to `MarketClosedState`'s much
 * shorter copy. Resolving status HERE, before the screen ever mounts, closes
 * that gap: `showClosed` is already known on the screen's first render.
 *
 * CLOSED: the books are not requested at all. mm-be answers depth with a 409
 * MARKET_CLOSED for the entire closed session — the same reason
 * `useMarketDepth` gates itself on `enabled: !isPending && isOpen`, and an
 * imperative call here has no `enabled` of its own to honour that gate.
 *
 * OPEN: `marketDepthQueryOptions`, built from the entry URL's search
 * (`depthSearchSchema` above, the same schema `validateSearch` uses, so the
 * two can never disagree about what a URL means) — the same key
 * `useMarketDepth` reads on mount. A MARKET_CLOSED 409 here is swallowed
 * rather than left to throw: it is the SAME race `DepthScreen`'s own
 * `closedMidFlight` exists for — the status read above can itself be a beat
 * stale — just reached one step earlier, and mm-be saying "closed" is not
 * "did not load". The query cache is left holding the failed entry, which
 * `useMarketDepth` reads on mount exactly as it would a refetch that failed
 * after a good load. Any OTHER failure is genuine and is left to throw, same
 * as every other converted screen's main read: `DepthScreen`'s own
 * `enabled`/`closedMidFlight` handling is otherwise unchanged by any of this
 * — this loader only decides whether to WARM that query ahead of mount.
 *
 * SECONDARY, started and not awaited, only on a real navigation (`!preload`):
 * the sector filter's own option list — the same factory Movers, 52-week and
 * Live market start the same way.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/`/`hooks/`
 * modules only — route loaders are not code-split, so anything imported here
 * ships in the entry chunk.
 */
export async function loadDepth({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  if (!preload) prefetchRouteData(queryClient, depthSectorsQueryOptions())

  const status = await ensureRouteData(queryClient, marketStatusQueryOptions())
  if (!marketStateIsOpen(status.state)) return

  const search = depthSearchSchema.parse(location.search)
  try {
    await ensureRouteData(
      queryClient,
      marketDepthQueryOptions({ ...search, symbols: search.symbols ?? [] }),
    )
  } catch (error) {
    if (!hasCode(error, 'MARKET_CLOSED')) throw error
  }
}
